import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChallengeStatus } from '@/api/generated';
import { useLanguage } from '@/contexts/language-context';
import { loginPasswordSubmittedEvent } from '@/features/login-analytics/telemetry';
import type { PasswordFormData } from '@/form-schemas/password-schemas';
import {
  passwordSchema,
  passwordFormDefaults,
} from '@/form-schemas/password-schemas';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';

interface UsePasswordFormProps {
  submitPassword: UseMfaOrchestratorReturn['submitResponse'];
}

export function usePasswordForm({ submitPassword }: UsePasswordFormProps) {
  const { t } = useLanguage();

  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: passwordFormDefaults,
    mode: 'onSubmit', // Validate on submit, then revalidate on change
    reValidateMode: 'onChange', // After first submit, validate on every change
  });

  const { formState, setError, clearErrors, reset } = form;
  const { isSubmitting, errors } = formState;

  const onSubmit = async (data: PasswordFormData) => {
    try {
      clearErrors();

      const response = await submitPassword({
        password: data.password,
      });

      const errorStatuses = [
        ChallengeStatus.FAILED,
        ChallengeStatus.REJECTED,
        ChallengeStatus.RETRY_REQUIRED,
      ];

      if (errorStatuses.includes(response as ChallengeStatus)) {
        if (response === ChallengeStatus.RETRY_REQUIRED) {
          reset(passwordFormDefaults);
        }

        setError('root', {
          type: 'manual',
          message: t('auth.invalidCredentials'),
        });
      }
    } catch (_error) {
      setError('root', {
        type: 'manual',
        message: t('auth.invalidCredentials'),
      });
    }

    loginPasswordSubmittedEvent();
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    errors,
    getErrorMessage: () => {
      if (errors.password) {
        return errors.password.message;
      }

      if (errors.root) {
        return errors.root.message;
      }

      return '';
    },
  };
}
